import {
  useRentalManagerUserPropertyMultiplexWebApiHandler_create,
  useRentalManagerUserPropertyMultiplexWebApiHandler_units,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { MULTIPLEX_MUTATION_INVALIDATES } from '../constants/FlexiblePropertiesConstants';

import { handleMultiplexCreateResponse } from './useMultiplexUnitCreation';

import type {
  UserPropertyMultiplexCreateUnitRequestWebDto,
  UserPropertyMultiplexUnitResponseWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { MultiplexCreateCallbacks } from './useMultiplexUnitCreation';

type UseMultiplexUnitEditParams = {
  groupId: string;
  fullAddress: string;
};

export const useMultiplexUnitEdit = ({ groupId, fullAddress }: UseMultiplexUnitEditParams) => {
  const {
    data,
    isLoading: isLoadingUnits,
    isError: isErrorUnits,
  } = useRentalManagerUserPropertyMultiplexWebApiHandler_units(
    { groupId, includeUnarchived: true },
    {
      meta: {
        tags: ['getMultiplexUnits'],
        context: { api: 'rental-manager', details: 'useMultiplexUnitEdit' },
      },
    },
  );

  const existingUnits: Array<UserPropertyMultiplexUnitResponseWebDto> = data?.units ?? [];
  const existingUnitNumbers = existingUnits.map((u) => u.unitNumber);

  const { isPending, mutate: createMultiplexMutation } = useRentalManagerUserPropertyMultiplexWebApiHandler_create({
    meta: {
      invalidates: [...MULTIPLEX_MUTATION_INVALIDATES],
      context: { api: 'rental-manager', details: 'useMultiplexUnitEdit' },
    },
  });

  const createUnits = (
    units: Array<UserPropertyMultiplexCreateUnitRequestWebDto>,
    callbacks: MultiplexCreateCallbacks,
  ) => {
    createMultiplexMutation(
      { postData: { fullAddress, units } },
      {
        onSuccess: (response) => handleMultiplexCreateResponse(response, callbacks),
        onError: (error) => {
          callbacks.onError(DatarouterError.getDisplayMessage(error));
        },
      },
    );
  };

  return {
    existingUnits,
    existingUnitNumbers,
    isLoadingUnits,
    isErrorUnits,
    isPending,
    createUnits,
  };
};
